import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Paper,
  Stack,
  Table,
  TableCell,
  TableContainer,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { IconButton } from '@components/base';
import { NodeIdentity, NodeQueryResult, NodeStatistics } from '@models/NodeStatus';

export function NodeIdentityCell({ node }: { node: NodeStatistics }) {
  return (
    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>
      <Tooltip title={`${node.nodeId} - ${dayjs(node.startedAt).format('YYYY-MM-DD HH:mm:ss')}`}>
        <span>{node.nodeId}</span>
      </Tooltip>
    </TableCell>
  );
}

export default function NodeStatusPanel<T extends NodeIdentity>({
  actions,
  children,
  data,
  isError,
  isFetching,
  onRefresh,
}: {
  actions?: ReactNode;
  children: ReactNode;
  data?: NodeQueryResult<T>;
  isError: boolean;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2}>
      <Stack alignItems='center' direction='row' justifyContent='space-between'>
        <Stack alignItems='center' direction='row' spacing={1}>
          <Typography variant='h2'>{t('nodes.title')}</Typography>
          <IconButton
            aria-label={t('nodes.refresh')}
            icon='replay'
            loading={isFetching}
            onClick={onRefresh}
            sx={{ '& .MuiSvgIcon-root': { opacity: isFetching ? 0 : 1 } }}
            title={t('nodes.refresh')}
          />
        </Stack>
        {actions}
      </Stack>
      {isError && <Alert severity='error'>{t('nodes.error')}</Alert>}
      {data && data.missingNodeIds.length > 0 && (
        <Alert severity='warning'>
          {t('nodes.missing', { nodes: data.missingNodeIds.join(', ') })}
        </Alert>
      )}
      {data?.transportInterrupted && <Alert severity='warning'>{t('nodes.interrupted')}</Alert>}
      {data && data.nodes.length === 0 && (
        <Alert severity='warning'>{t('nodes.noResponses')}</Alert>
      )}
      <TableContainer component={Paper}>
        <Table aria-label={t('nodes.title')} size='small'>
          {children}
        </Table>
      </TableContainer>
    </Stack>
  );
}
